import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import React, { useEffect, useMemo } from 'react'
import { usePurchaseFlowConfig, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts'

import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'
import { HeatingSystemItem } from './HeatingSystemItem'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import Stack from '@mui/material/Stack'
import { TransitionGroup } from 'react-transition-group'
import { useHeatingSystem } from './HeatingSystem.context'
import { useTranslation } from 'react-i18next'

/**
 * @component HeatingSystem renders a list of heating system items and provides functionality
 * to add new items.
 *
 * @example
 *   <HeatingSystem />
 */
export const HeatingSystem: React.FC = () => {

  const { t } = useTranslation(['heating_system'])

  const {
    selectedProducts,
    selectedKinds,
    selectedProductTypes,
  } = usePurchaseFlowProducts()

  const { selectedCategory } = usePurchaseFlowConfig()

  const { heatingSystemItems, handleAddHeatingSystemItem } = useHeatingSystem()

  /** Sorted heating system items by their IDs considering numeric values for proper ordering. */
  const sortedHeatingSystemItems = useMemo(() => {
    if (!heatingSystemItems) return
    const arrayHeatinngSystemItems = heatingSystemItems.toArray()

    return arrayHeatinngSystemItems.sort(([idA], [idB]) => idA.localeCompare(idB, undefined, { numeric: true }))
  }, [heatingSystemItems])

  useEffect(() => {
    logAnalyticsEvent(AnalyticsEvent.OPENS_HEATING_SYSTEM, {
      productTypes: Array.from(selectedProductTypes),
      productKinds: Array.from(selectedKinds),
      products: Object.values(selectedProducts),
      category: selectedCategory,
    })
    // Only trigger when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Fade in={true}>
      <Stack gap={2}>
        {/** Heating system items */}
        <TransitionGroup component={null}>
          {sortedHeatingSystemItems && sortedHeatingSystemItems.map(([id], index) =>
            <Collapse key={id}>
              <HeatingSystemItem id={id} index={index} />
            </Collapse>
          )}
        </TransitionGroup>

        {/** Add new items */}
        <MUIDivider>
          <MUIButton
            type='secondaryBorder'
            startIcon={<AddCircleOutlineRoundedIcon fontSize='large' />}
            onClick={() => handleAddHeatingSystemItem()}
          >
            {t('add_heating_system')}
          </MUIButton>
        </MUIDivider>

      </Stack>
    </Fade>
  )
}
